import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useContext } from 'react';
import * as api from '../../services/api';
import useAccount from '../account/useAccount';
import useToaster from '../useToaster';

const useEmployeePto = ({ employeeId, employeeContractId }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { addSuccess } = useToaster();

  const queryClient = useQueryClient();
  const storeKey = ['employeePto', accountId, employeeId, employeeContractId];

  const { isLoading, data } = useQuery(
    storeKey,
    () =>
      api.fetchEmployeeContractPtoInfo({
        accountId,
        employeeId,
        employeeContractId
      }),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!accountId && !!employeeId && !!employeeContractId
    }
  );

  const { mutateAsync: addPtoDays } = useMutation(
    ({ startDate, endDate, type, branchId }) =>
      api.addPtoDays({
        accountId,
        employeeId,
        branchId,
        type,
        employeeContractId,
        startDate,
        endDate
      }),
    {
      onSuccess: () => {
        addSuccess(t('ptoAddedSuccessfully'));
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mutateAsync: addPtoBalanceAdjustment } = useMutation(
    ({ amount, reason, type }) =>
      api.addEmployeePtoBalanceAdjustment({
        accountId,
        employeeId,
        employeeContractId,
        amount,
        reason,
        type
      }),
    {
      onSuccess: () => {
        addSuccess(t('employeePtoAdjustmentAddedSuccessfully'));
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mutateAsync: deletePto } = useMutation(
    (ptoId) =>
      api.removePtoForPayrollQuery({
        accountId,
        ptoId
      }),
    {
      onSuccess: () => {
        addSuccess(t('ptoDeletedSuccessfully'));
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mutateAsync: employeeUseAllPtoBalance } = useMutation(
    ({ branchId, date }) =>
      api.useAllPtoBalance({
        accountId,
        employeeId,
        employeeContractId,
        branchId,
        date
      }),
    {
      onSuccess: () => {
        addSuccess(t('ptoAddedSuccessfully'));
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  return {
    isLoading,
    data: data?.data || { ptos: [], balance: {}, hasFutureJobs: false },
    addPtoDays,
    addPtoBalanceAdjustment,
    deletePto,
    employeeUseAllPtoBalance
  };
};

export default useEmployeePto;
